import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Observable, Subscription} from 'rxjs';
import {AllergyAddCardComponent} from './allergy-add-card/allergy-add-card.component';
import {AppMatAutocompleteComponent} from '@xmedical/ui';
import {PatientService} from '../../patients/services/patient.service';
import {CoreService} from 'src/app/core/services/core.service';
import {PatientEvent, PatientEventsService} from '../../patients/services/patient-events.service';
import {filter, map} from 'rxjs/operators';
import {AllergiesRepositoryService, Allergy, UserSettings, Patient, WebSocketService, Events} from '@xmedical/core';

@Component({
    selector: 'app-allergies-widget',
    templateUrl: './allergies-widget.component.html',
    styleUrl: './allergies-widget.component.scss',
    standalone: false
})
export class AllergiesWidgetComponent implements OnInit, OnDestroy {

  error = '';
  loading = false;
  settings: UserSettings;
  allergies: Allergy[] = [];
  @Input() readonly = false;
  @ViewChild(AppMatAutocompleteComponent) autocomplete: AppMatAutocompleteComponent;
  private subscriptions: Subscription[] = [];
  private channelName = '';
  private readonly eventName = Events.AllergyListAllergyListChanged;

  constructor(
    private matDialog: MatDialog,
    private allergyRepository: AllergiesRepositoryService,
    private patientService: PatientService,
    private coreService: CoreService,
    private pushService: WebSocketService,
    private patientEventService: PatientEventsService
  ) {
  }

  get hasError(): boolean {
    return this.error.length > 0;
  }

  get hasAllergies(): boolean {
    return this.allergies.length > 0;
  }

  get patientId(): number {
    return this.patientService.currentPatient.id;
  }

  get patient(): Patient {
    return this.patientService.currentPatient;
  }

  searchAllergies$ = (query: string): Observable<Allergy[]> => {
    return this.allergyRepository.query(query)
      .pipe(map(response => response));
  }

  ngOnInit(): void {
    this.channelName = this.patientService.currentPatientEventsChannelName;
    this.settings = this.coreService.settings();
    this.subscriptions.push(this.patientEventService.currentPatient$.subscribe(() => this.loadPatientAllergies()));
    this.subscriptions.push(this.patientService.patientAllergies$.subscribe(allergies => this.allergies = [...allergies]));
    this.subscriptions.push(this.patientEventService.events$.pipe(filter(e => e.type === 'allergy_item_deleted')).subscribe(() => this.loadPatientAllergies()));
    this.pushService.subscribe(this.channelName, this.eventName, data => {
      console.log('AllergiesWidgetComponent: pushMessageReceived', data);
      this.loadPatientAllergies();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.pushService.unsubscribe(this.channelName, this.eventName);
  }

  onAddNewItem() {
    const dialogRef = this.matDialog.open(AllergyAddCardComponent, {
      panelClass: 'mat-confirmation-dialog-responsive'
    });
    this.subscriptions.push(dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.autocomplete.search(result.allergy);
      }
    }));
  }

  onItemSelected(allergy: Allergy) {
    this.allergies.push(allergy);
    this.addAllergyToPatient(allergy);
    this.autocomplete.cancelSearch();
  }

  private loadPatientAllergies() {
    if (!this.patientService.hasPatientDetails) {
      return;
    }
    this.patientService.loadPatientAllergies();
  }

  private addAllergyToPatient(allergy: Allergy) {
    this.error = '';
    this.subscriptions.push(this.allergyRepository.addAllergyToPatient(this.patientId, allergy).subscribe(() => {
      this.patientEventService.emitEvent(new PatientEvent('allergy_item_added'));
    }, error => {
      this.error = 'Could not add new allergy to this patient';
      this.loadPatientAllergies();
    }));
  }

}
